import { useState, useMemo } from 'react';
import { serializeMarkSequence } from '../lib/courseParser.js';
import { legDetails } from '../lib/geo.js';
import { DIRECTION_LABELS, WIND_SECTOR_LABELS, courseDisplayLabel } from '../lib/courseConstants.js';

const COURSE_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706',
  '#7c3aed', '#0891b2', '#be185d', '#65a30d',
];

export function getCourseColor(index) {
  return COURSE_COLORS[index % COURSE_COLORS.length];
}

/**
 * Group courses by cardName. Returns [{cardName, courses}].
 * Courses without a cardName go into a single "Courses" group.
 */
function groupByCard(cards) {
  const map = new Map();
  for (const card of cards) {
    const key = card.cardName || '_ungrouped';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(card);
  }
  return Array.from(map.entries()).map(([key, courses]) => ({
    cardName: key === '_ungrouped' ? 'Courses' : key,
    isUngrouped: key === '_ungrouped',
    courses,
  }));
}

function hasCoords(mark) {
  return (
    typeof mark.latitude === 'number' &&
    typeof mark.longitude === 'number' &&
    !(mark.latitude === 0 && mark.longitude === 0)
  );
}

function buildMarkLookup(marks) {
  const lookup = {};
  for (const m of marks) {
    if (hasCoords(m)) lookup[m.id] = [m.latitude, m.longitude];
  }
  return lookup;
}

/** Compact bearing display: 0° → "N", 45° → "NE", etc. */
function compassLabel(deg) {
  if (deg == null) return '?';
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

/** Expandable detail view for a focused course. */
function CourseDetail({ card, marks }) {
  const lookup = useMemo(() => buildMarkLookup(marks), [marks]);
  const legs = useMemo(() => legDetails(card.markIds, lookup), [card.markIds, lookup]);

  const totalDist = legs.reduce((sum, l) => sum + (l.distNm || 0), 0);
  // Grid courses carry a 16-point directionCode; named courses carry an 8-point
  // wind sector (windDirection). Show whichever this course has. (ra-gbrd.35)
  const windLabel = card.directionCode
    ? DIRECTION_LABELS[card.directionCode] || card.directionCode
    : card.windDirection
      ? WIND_SECTOR_LABELS[card.windDirection] || card.windDirection
      : null;

  return (
    <div className="course-detail">
      {windLabel && (
        <div className="course-detail-wind">Wind: {windLabel}</div>
      )}
      <table className="course-detail-legs">
        <thead>
          <tr>
            <th>#</th>
            <th>From</th>
            <th>To</th>
            <th>Rnd</th>
            <th>Brg</th>
            <th>Dist</th>
          </tr>
        </thead>
        <tbody>
          {legs.map((leg, i) => {
            const rounding = card.markDirections?.[i] || '';
            return (
              <tr key={i}>
                <td className="leg-num">{i + 1}</td>
                <td>{leg.from}</td>
                <td>{leg.to}</td>
                <td className={`leg-rounding ${rounding}`}>
                  {rounding === 'port' ? 'P' : rounding === 'starboard' ? 'S' : ''}
                </td>
                <td>{leg.bearing != null ? `${Math.round(leg.bearing)}° ${compassLabel(leg.bearing)}` : '—'}</td>
                <td>{leg.distNm != null ? `${leg.distNm.toFixed(2)}` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="5" className="total-label">Total</td>
            <td>{totalDist > 0 ? `${totalDist.toFixed(2)} nm` : '—'}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function CourseListPanel({
  cards,
  selectedIds,
  onToggle,
  onToggleSection,
  focusedId,
  onFocus,
  fleetMap,
  marks = [],
  collapsed,
  onToggleCollapse,
  startNm = 0,
  finishNm = 0,
}) {
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  const groups = useMemo(() => groupByCard(cards), [cards]);
  const hasGroups = groups.length > 1 || (groups.length === 1 && !groups[0].isUngrouped);

  const toggleSection = (cardName) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(cardName)) {
        next.delete(cardName);
      } else {
        next.add(cardName);
      }
      return next;
    });
  };

  // Global course index for consistent coloring across groups
  let globalIndex = 0;

  return (
    <div className={`course-panel ${collapsed ? 'collapsed' : ''}`}>
      <button className="panel-toggle" onClick={onToggleCollapse}>
        {collapsed ? '\u25B6' : '\u25C0'} Courses
        {!collapsed && <span className="badge">{cards.length}</span>}
      </button>

      {!collapsed && (
        <div className="panel-body">
          {cards.length === 0 && (
            <p className="empty-state">No courses match filters.</p>
          )}
          {groups.map((group) => {
            const sectionCollapsed = collapsedSections.has(group.cardName);
            const courseIds = group.courses.map((c) => c.id);
            const allSelected = courseIds.length > 0 && courseIds.every((id) => selectedIds.has(id));
            const someSelected = courseIds.some((id) => selectedIds.has(id));

            // Track starting index for this group
            const groupStartIndex = globalIndex;
            globalIndex += group.courses.length;

            return (
              <div key={group.cardName} className="course-section">
                {hasGroups && (
                  <div className="section-header">
                    <button
                      className="section-toggle"
                      onClick={() => toggleSection(group.cardName)}
                    >
                      <span className="section-toggle-arrow">{sectionCollapsed ? '\u25B6' : '\u25BC'}</span>
                      <span className="section-toggle-label">
                        {group.cardName}
                        <span className="section-count">({group.courses.length})</span>
                      </span>
                    </button>
                    {!sectionCollapsed && onToggleSection && (
                      <label className="section-select-all">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={() => onToggleSection(group.cardName, courseIds, allSelected)}
                        />
                        <span className="select-all-label">All</span>
                      </label>
                    )}
                  </div>
                )}

                {!sectionCollapsed && group.courses.map((card, i) => {
                  const idx = groupStartIndex + i;
                  const markSeq = card.markIds.map((id, j) => ({
                    id,
                    rounding: card.markDirections[j] || 'port',
                  }));
                  const seq = serializeMarkSequence(markSeq);
                  const selected = selectedIds.has(card.id);
                  const focused = focusedId === card.id;
                  const color = getCourseColor(idx);
                  const fleet = fleetMap[card.id];

                  return (
                    <div key={card.id} className={`course-item-wrap ${focused ? 'focused' : ''}`}>
                      <label className={`course-item ${selected ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => onToggle(card.id)}
                        />
                        <span className="course-swatch" style={{ background: color }} />
                        <div
                          className="course-info"
                          onClick={(e) => {
                            e.preventDefault();
                            if (onFocus) onFocus(card.id);
                          }}
                        >
                          <span className="course-route">
                            {courseDisplayLabel(card)}
                            {focused && <span className="focus-indicator" title="Focused on map" />}
                          </span>
                          <span className="course-seq">{seq}</span>
                          <span className="course-meta">
                            {card.distance > 0 && <>
                              {(card.distance + startNm + finishNm).toFixed(1)} nm
                              <span className="card-distance">
                                ({card.distance.toFixed(1)})
                              </span>
                            </>}
                            {fleet && <span className="fleet-tag">{fleet}</span>}
                          </span>
                        </div>
                      </label>
                      {focused && <CourseDetail card={card} marks={marks} />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
